import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Star, MapPin, Calendar, User, Camera, Video, 
  Award, Briefcase, Share2, Heart, Globe, Phone, Music,
  Instagram, Facebook, Youtube, Linkedin, Twitter, CheckCircle,
  ExternalLink, Mail, MessageSquare, Link2, Copy, Users, Handshake, Building,
  Package, Clock
} from "lucide-react";
import { getProfileShareUrlAsync, handleShare as shareHelper, safeCopyToClipboard } from "@/utils/shareHelper";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import { PublicProfileShareHint } from "@/components/OnboardingHints";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function ProfileView() {
  const { profileId, slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRating, setMyRating] = useState(null);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLinks, setProfileLinks] = useState([]);
  const [linkRequestSent, setLinkRequestSent] = useState(false);
  const [inTalentBoard, setInTalentBoard] = useState(false);
  const [talentBoardEntryId, setTalentBoardEntryId] = useState(null);
  const [addingToBoard, setAddingToBoard] = useState(false);

  useEffect(() => {
    fetchProfile();
    checkAuth();
  }, [profileId, slug]);

  useEffect(() => {
    if (currentUser?.role === "enterprise" && profile?.profile_id) {
      checkTalentBoard();
    }
  }, [currentUser, profile]);

  const checkTalentBoard = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/talentboard/check/${profile.profile_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInTalentBoard(data.in_board);
        setTalentBoardEntryId(data.entry_id);
      }
    } catch (error) {
      console.error("Error checking TalentBoard:", error);
    }
  };

  const handleAddToTalentBoard = async () => {
    if (addingToBoard) return;
    setAddingToBoard(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/talentboard/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ profile_id: profile.profile_id })
      });
      if (response.ok) {
        const data = await response.json();
        setInTalentBoard(true);
        setTalentBoardEntryId(data.entry?.id);
        toast.success("Added to TalentBoard!");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add to TalentBoard");
    } finally {
      setAddingToBoard(false);
    }
  };

  const fetchProfile = async () => {
    try {
      let response;
      if (slug) {
        response = await fetch(`${API_URL}/api/p/${slug}`);
        if (response.ok) {
          const data = await response.json();
          if (data.redirect && data.new_slug) {
            navigate(`/p/${data.new_slug}`, { replace: true });
            return;
          }
          setProfile(data);
          fetchProfileLinks(data.profile_id);
          return;
        }
      }
      if (profileId) {
        response = await fetch(`${API_URL}/api/public/profile/${profileId}`);
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          fetchProfileLinks(data.profile_id);
          return;
        }
        response = await fetch(`${API_URL}/api/profiles/${profileId}`);
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          fetchProfileLinks(data.profile_id);
          return;
        }
      }
      toast.error("Profile not found");
      navigate("/browse");
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileLinks = async (pid) => {
    try {
      const response = await fetch(`${API_URL}/api/profiles/${pid}/links`);
      if (response.ok) {
        const data = await response.json();
        setProfileLinks(data.links || []);
      }
    } catch (error) {
      console.error("Error fetching profile links:", error);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      setIsLoggedIn(true);
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
        }
      } catch (error) {
        console.error("Error checking auth:", error);
      }
      if (profile?.profile_id) {
        fetchMyRating(token);
      }
    }
  };

  const fetchMyRating = async (token) => {
    const pid = profile?.profile_id || profileId;
    if (!pid) return;
    try {
      const response = await fetch(`${API_URL}/api/profiles/${pid}/my-rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyRating(data.rating);
      }
    } catch (error) {
      console.error("Error fetching rating:", error);
    }
  };

  const copyProfileUrl = async () => {
    const url = await getProfileShareUrlAsync(profile, profile?.profile_type || "talent");
    const success = await safeCopyToClipboard(url);
    if (success) {
      toast.success("Profile URL copied!");
    } else {
      toast.info("Copy this URL: " + url, { duration: 10000 });
    }
  };

  const handleRate = async (rating) => {
    if (!isLoggedIn) {
      toast.error("Please login to rate profiles");
      navigate("/login");
      return;
    }
    if (!profile?.visibility?.allow_ratings) {
      toast.error("This profile doesn't accept ratings");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/profiles/${profileId}/rate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rating })
      });
      if (response.ok) {
        const data = await response.json();
        setMyRating(rating);
        setProfile(prev => ({
          ...prev,
          average_rating: data.average_rating,
          total_ratings: data.total_ratings
        }));
        toast.success("Rating submitted!");
      } else {
        throw new Error("Failed to rate");
      }
    } catch (error) {
      toast.error("Failed to submit rating");
    }
  };

  const handleShare = async () => {
    const url = await getProfileShareUrlAsync(profile, profile?.profile_type || "talent");
    const name = profile?.personal_info?.display_name || profile?.personal_info?.full_name || "Profile";
    await shareHelper({
      url,
      title: `${name} - TalentMeet`,
      text: `View ${name}'s profile on TalentMeet`
    });
  };

  const handleRequestCollaboration = async () => {
    if (!isLoggedIn) {
      toast.error("Please login to send collaboration requests");
      navigate("/login");
      return;
    }
    if (currentUser?.role !== "enterprise") {
      toast.error("Only Enterprise accounts can send collaboration requests");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/links/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          target_profile_id: profile.profile_id,
          message: `I'd like to connect and collaborate with you.`
        })
      });
      if (response.ok) {
        toast.success("Collaboration request sent!");
        setLinkRequestSent(true);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send request");
      }
    } catch (error) {
      toast.error("Failed to send collaboration request");
    }
  };

  const canRequestCollaboration = () => {
    if (!isLoggedIn || !currentUser) return false;
    if (currentUser.role !== "enterprise") return false;
    if (!profile) return false;
    const profileType = profile.profile_type || profile.user_role;
    return ["talent", "trainer"].includes(profileType);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-6 h-6 border-2 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Profile not found</p>
      </div>
    );
  }

  const personal = profile.personal_info || {};
  const professional = profile.professional_info || {};
  const portfolio = profile.portfolio || {};
  const social = profile.social_presence || {};
  const contact = profile.contact_management || {};
  const enterprise = profile.enterprise_info || {};
  const trainer = profile.trainer_info || {};
  const allPhotos = [portfolio.profile_photo, ...(portfolio.additional_photos || [])].filter(Boolean);
  const firstName = (personal.display_name || personal.full_name || "").split(" ")[0];

  // Check if any social links exist
  const hasSocialLinks = social.instagram || social.youtube || social.facebook || social.twitter || social.linkedin || social.imdb || social.personal_website;

  // Check if user is viewing their own profile
  const isViewingOwnProfile = isLoggedIn && currentUser && profile && 
    (currentUser.user_id === profile.user_id || 
     currentUser.profile?.profile_id === profile.profile_id);

  // Navigate to edit profile based on role
  const handleEditProfile = () => {
    const role = currentUser?.role || profile?.profile_type;
    if (role === "talent") navigate("/talent/wizard");
    else if (role === "trainer") navigate("/trainer/wizard");
    else if (role === "enterprise") navigate("/enterprise/wizard");
    else navigate("/profile/edit");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn" className="h-8 w-8 p-0">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-7 w-7 rounded-lg" />
                <span className="font-semibold text-gray-900 hidden sm:block">TalentMeet</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentUser?.role === "enterprise" && profile?.profile_type !== "enterprise" && (
                inTalentBoard ? (
                  <Button size="sm" variant="outline" onClick={() => navigate("/enterprise/talentboard")} 
                    className="h-8 text-xs" data-testid="view-talentboard-btn">
                    In TalentBoard
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleAddToTalentBoard} disabled={addingToBoard}
                    className="h-8 text-xs bg-teal-600 hover:bg-teal-700" data-testid="add-talentboard-btn">
                    + TalentBoard
                  </Button>
                )
              )}
              <Button variant="outline" size="sm" onClick={handleShare} className="h-8 text-xs" data-testid="share-btn">
                <Share2 className="w-3.5 h-3.5 mr-1" /> Share
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Single Continuous Scroll */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-4 space-y-4">
        
        {/* ===== ONBOARDING HINT: Complete your profile ===== */}
        {isViewingOwnProfile && (
          <PublicProfileShareHint 
            onEditClick={handleEditProfile}
            className="animate-in fade-in slide-in-from-top-2 duration-300"
          />
        )}
        
        {/* ===== SECTION 1: HERO ===== */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Profile Photo */}
              <div className="flex-shrink-0 mx-auto sm:mx-0" style={{ maxWidth: '200px' }}>
                <div className="relative aspect-[4/5] bg-gray-100 rounded-xl overflow-hidden">
                  {allPhotos.length > 0 ? (
                    <img src={allPhotos[selectedPhoto]} alt={personal.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  {profile.admin_fields?.verification_status === "verified" && (
                    <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1 shadow-sm">
                      <CheckCircle className="w-3 h-3 text-teal-600" />
                      <span className="text-xs font-medium text-teal-700">Verified</span>
                    </div>
                  )}
                </div>
                {/* Photo Thumbnails */}
                {allPhotos.length > 1 && (
                  <div className="flex gap-1.5 mt-2 overflow-x-auto justify-center">
                    {allPhotos.slice(0, 4).map((photo, i) => (
                      <button key={i} onClick={() => setSelectedPhoto(i)}
                        className={`flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden transition-all ${selectedPhoto === i ? 'ring-2 ring-violet-500 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}>
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                    {allPhotos.length > 4 && (
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                        +{allPhotos.length - 4}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center sm:text-left">
                <h1 className="text-xl font-semibold text-gray-900">
                  {personal.display_name || personal.full_name}
                </h1>
                <p className="text-sm text-violet-600 font-medium">{professional.primary_category || "Talent"}</p>
                
                {professional.headline && (
                  <p className="text-sm text-gray-600 mt-1">{professional.headline}</p>
                )}

                <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2 text-xs text-gray-500">
                  {personal.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {personal.city}{personal.country ? `, ${personal.country}` : ''}
                    </span>
                  )}
                  {professional.years_of_experience > 0 && (
                    <Badge variant="secondary" className="text-xs">{professional.years_of_experience} Yrs Exp</Badge>
                  )}
                </div>

                {/* Rating */}
                {profile.visibility?.allow_ratings && (
                  <div className="flex items-center justify-center sm:justify-start gap-2 mt-2">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold text-sm">{profile.average_rating?.toFixed(1) || "0.0"}</span>
                      <span className="text-xs text-gray-400">({profile.total_ratings || 0})</span>
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button key={star} onClick={() => handleRate(star)}
                          onMouseEnter={() => setHoveredStar(star)} onMouseLeave={() => setHoveredStar(0)}
                          className="p-0.5" data-testid={`rate-star-${star}`}>
                          <Star className={`w-3.5 h-3.5 ${star <= (hoveredStar || myRating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
                  {canRequestCollaboration() && (
                    <Button size="sm" onClick={handleRequestCollaboration} disabled={linkRequestSent}
                      className="h-8 text-xs" data-testid="request-collaboration-btn">
                      <Handshake className="w-3.5 h-3.5 mr-1" />
                      {linkRequestSent ? "Sent" : "Collaborate"}
                    </Button>
                  )}
                  <Button size="sm" className="h-8 text-xs" data-testid="profile-chat-btn"
                    onClick={() => { if (!isLoggedIn) { navigate("/login"); return; } navigate(`/chat?userId=${profile.user_id}`); }}>
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Message
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={copyProfileUrl}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ===== SECTION 2: SOCIAL LINKS (below photo) ===== */}
        {hasSocialLinks && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 text-center mb-3">Check out my social links below</p>
              <div className="flex flex-wrap justify-center gap-2">
                {social.instagram && (
                  <a href={social.instagram.startsWith('http') ? social.instagram : `https://instagram.com/${social.instagram}`} 
                    target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {social.youtube && (
                  <a href={social.youtube.startsWith('http') ? social.youtube : `https://youtube.com/${social.youtube}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <Youtube className="w-4 h-4" />
                  </a>
                )}
                {social.facebook && (
                  <a href={social.facebook.startsWith('http') ? social.facebook : `https://facebook.com/${social.facebook}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
                {social.twitter && (
                  <a href={social.twitter.startsWith('http') ? social.twitter : `https://twitter.com/${social.twitter}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-900 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <Twitter className="w-4 h-4" />
                  </a>
                )}
                {social.linkedin && (
                  <a href={social.linkedin.startsWith('http') ? social.linkedin : `https://linkedin.com/in/${social.linkedin}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
                {social.imdb && (
                  <a href={social.imdb} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-yellow-500 flex items-center justify-center text-black font-bold text-xs hover:opacity-80 transition-opacity">
                    IMDb
                  </a>
                )}
                {social.personal_website && (
                  <a href={social.personal_website} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <Globe className="w-4 h-4" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 3: ABOUT / BIO ===== */}
        {professional.bio && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" /> About
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{professional.bio}</p>
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 4: SKILLS & TAGS ===== */}
        {(professional.skills?.length > 0 || personal.languages?.length > 0 || professional.acting_styles?.length > 0 || professional.dance_forms?.length > 0) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Skills & Expertise</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {professional.skills?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Skills</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {professional.skills.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {personal.languages?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Languages</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {personal.languages.map((lang, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{lang}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {professional.acting_styles?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Acting Styles</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {professional.acting_styles.map((s, i) => <Badge key={i} className="text-xs bg-violet-100 text-violet-700">{s}</Badge>)}
                  </div>
                </div>
              )}
              {professional.dance_forms?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Dance Forms</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {professional.dance_forms.map((d, i) => <Badge key={i} className="text-xs bg-pink-100 text-pink-700">{d}</Badge>)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 5: PHYSICAL ATTRIBUTES ===== */}
        {(personal.height_cm || personal.weight_kg || personal.body_type || personal.skin_tone || personal.eye_color || personal.hair_color) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Physical Attributes</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-3 text-sm">
                {personal.height_cm && <div><span className="text-xs text-gray-500">Height</span><p className="font-medium">{personal.height_cm} cm</p></div>}
                {personal.weight_kg && <div><span className="text-xs text-gray-500">Weight</span><p className="font-medium">{personal.weight_kg} kg</p></div>}
                {personal.body_type && <div><span className="text-xs text-gray-500">Build</span><p className="font-medium">{personal.body_type}</p></div>}
                {personal.skin_tone && <div><span className="text-xs text-gray-500">Skin</span><p className="font-medium">{personal.skin_tone}</p></div>}
                {personal.eye_color && <div><span className="text-xs text-gray-500">Eyes</span><p className="font-medium">{personal.eye_color}</p></div>}
                {personal.hair_color && <div><span className="text-xs text-gray-500">Hair</span><p className="font-medium">{personal.hair_color}</p></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 6: OFFERINGS (Trainer/Enterprise) ===== */}
        {((profile.profile_type === "trainer" && trainer.offerings?.length > 0) || 
          (profile.profile_type === "enterprise" && enterprise.offerings?.length > 0)) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Package className="w-4 h-4" /> 
                {profile.profile_type === "trainer" ? "Classes & Offerings" : "Packages & Services"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {(profile.profile_type === "trainer" ? trainer.offerings : enterprise.offerings)?.map((off, i) => (
                <div key={i} className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium">{off.title}</h4>
                      {off.description && <p className="text-xs text-gray-600 mt-0.5">{off.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-1 text-xs">
                        {off.duration && <span className="flex items-center gap-1 text-gray-500"><Clock className="w-3 h-3" /> {off.duration}</span>}
                        {off.price && <span className="font-medium text-violet-700">{off.price}</span>}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs ml-2">
                      {off.mode === "both" ? "Online & Offline" : off.mode || "Offline"}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 7: ENTERPRISE INFO ===== */}
        {profile.profile_type === "enterprise" && (enterprise.services?.length > 0 || enterprise.notable_clients?.length > 0) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building className="w-4 h-4" /> Company Info
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {enterprise.services?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Services</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {enterprise.services.map((s, i) => <Badge key={i} className="text-xs bg-blue-100 text-blue-700">{s}</Badge>)}
                  </div>
                </div>
              )}
              {enterprise.notable_clients?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Notable Clients</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {enterprise.notable_clients.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 8: WORK / CREDITS ===== */}
        {professional.past_work?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Work & Credits
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {professional.past_work.map((work, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">{work.project_name || work.project || work.title}</p>
                    {work.role && <p className="text-xs text-gray-500">as {work.role}</p>}
                  </div>
                  <div className="text-right">
                    {work.year && <p className="text-xs text-gray-500">{work.year}</p>}
                    {work.platform && <Badge variant="secondary" className="text-xs">{work.platform}</Badge>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Awards */}
        {professional.awards?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Award className="w-4 h-4" /> Awards
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-1">
                {professional.awards.map((award, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Award className="w-3 h-3 text-yellow-500" /> {award}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Training */}
        {(professional.training?.length > 0 || professional.trainings?.length > 0) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Training & Education</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-1">
                {(professional.training || professional.trainings || []).map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-3 h-3 text-teal-500" /> {t}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 9: MEDIA (Gallery/Showreel) ===== */}
        {(portfolio.showreel || allPhotos.length > 1) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Video className="w-4 h-4" /> Media
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {/* Showreel */}
              {portfolio.showreel && (
                <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                  {portfolio.showreel.includes("youtube") || portfolio.showreel.includes("youtu.be") ? (
                    <iframe
                      src={portfolio.showreel.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Showreel"
                    ></iframe>
                  ) : (
                    <a href={portfolio.showreel} target="_blank" rel="noopener noreferrer"
                      className="w-full h-full flex items-center justify-center text-white hover:bg-gray-800">
                      <Video className="w-12 h-12" />
                    </a>
                  )}
                </div>
              )}
              {/* Photo Gallery */}
              {allPhotos.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {allPhotos.slice(1, 9).map((photo, i) => (
                    <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <img src={photo} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer" />
                    </div>
                  ))}
                  {allPhotos.length > 9 && (
                    <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center text-sm text-gray-500">
                      +{allPhotos.length - 9}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 10: CONTACT INFO ===== */}
        {(contact.contact_email || contact.contact_phone || (contact.managed_by === "Agency" && contact.agency_name)) && (
          <Card className="border-teal-100 bg-teal-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" /> Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {contact.contact_email && (
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-teal-600" />
                  <a href={`mailto:${contact.contact_email}`} className="text-sm text-teal-700 hover:underline">
                    {contact.contact_email}
                  </a>
                </div>
              )}
              {contact.contact_phone && (contact.phone_visible !== false) && (
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-teal-600" />
                  <a href={`tel:${contact.contact_phone}`} className="text-sm text-teal-700 hover:underline">
                    {contact.contact_phone}
                  </a>
                </div>
              )}
              {contact.managed_by === "Agency" && contact.agency_name && (
                <div className="mt-2 pt-2 border-t border-teal-200">
                  <p className="text-xs text-gray-600">Managed by Agency</p>
                  <p className="text-sm font-medium">{contact.agency_name}</p>
                  {contact.agent_contact && <p className="text-xs text-gray-600">{contact.agent_contact}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Collaborations */}
        {profileLinks.length > 0 && (
          <Card className="border-violet-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-violet-600" /> 
                {(profile.profile_type || profile.user_role) === "enterprise" ? "Team" : "Working With"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {profileLinks.map((link, i) => {
                const isEnterprise = (profile.profile_type || profile.user_role) === "enterprise";
                const linkedName = isEnterprise 
                  ? link.linked_profile?.personal_info?.full_name || link.target_name
                  : link.enterprise_profile?.personal_info?.company_name || link.enterprise_profile?.personal_info?.full_name || link.enterprise_name;
                const linkedSlug = isEnterprise ? link.linked_profile?.slug : link.enterprise_profile?.slug;
                
                return (
                  <div key={link.link_id || i} 
                    className="flex items-center justify-between p-2 bg-violet-50 rounded-lg hover:bg-violet-100 cursor-pointer"
                    onClick={() => linkedSlug ? navigate(`/p/${linkedSlug}`) : null}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-violet-200 flex items-center justify-center">
                        <Handshake className="w-4 h-4 text-violet-700" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{linkedName}</p>
                        <p className="text-xs text-violet-600">{link.relationship_type}</p>
                      </div>
                    </div>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* ===== SECTION 11: FINAL CTA BLOCK ===== */}
        <Card className="bg-gray-900 text-white">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold mb-2">
              Interested in working with {firstName || "this talent"}?
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Reach out to discuss opportunities and collaborations
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button size="sm" className="h-9 bg-white text-gray-900 hover:bg-gray-100" 
                onClick={() => { if (!isLoggedIn) { navigate("/login"); return; } navigate(`/chat?userId=${profile.user_id}`); }}>
                <MessageSquare className="w-4 h-4 mr-2" /> Message {firstName}
              </Button>
              <Button size="sm" variant="outline" className="h-9 border-white text-white hover:bg-white/10" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" /> Share Profile
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-4">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <img src={LOGO_URL} alt="" className="w-4 h-4 rounded opacity-60" />
            <span>Powered by <span className="font-medium text-gray-500">TalentMeet</span></span>
          </div>
        </div>
      </main>
      
      <WhatsAppSupport />
    </div>
  );
}
